import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Archive, ArchiveRestore, Copy, Download, Globe, KeyRound, Mail, RefreshCw, ShieldCheck, Trash2, UserMinus, Users } from 'lucide-react';
import {
  ApiError,
  addSubscription,
  cancelInvite,
  createInvite,
  deleteSubscription,
  eraseVisitor,
  erasureExportUrl,
  getCosts,
  getErasureRequests,
  listInvites,
  listMembers,
  listSubscriptions,
  qk,
  removeMember,
  resendInvite,
  rotateProjectKey,
  setMemberRole,
  setProjectArchived,
  updateProjectSettings,
  type CostProfile,
  type EmailCadence,
  type EmailSubscription,
  type MemberRow,
  type Role,
} from '../api.js';
import BilgiDugmesi from '../components/BilgiDugmesi.js';
import Badge from '../components/Badge.js';
import Card from '../components/Card.js';
import { ErrorState, sdkSnippet } from '../components/EmptyState.js';
import { kurulumTone, useIngestStatus } from '../components/KurulumDurumu.js';
import Table from '../components/Table.js';
import { useActivePid, useProjectContext } from '../components/Layout.js';
import { useRole } from '../components/AuthGate.js';
import {
  ROLES,
  ROLE_DESCRIPTIONS,
  ROLE_LABELS,
  canInviteMembers,
  isInviteExpired,
} from '../lib/auth.js';
import { formatAgo, formatDateTime, formatNumber, formatTL } from '../lib/format.js';

/**
 * §21.3 / §26.6 / §27.7 — Ayarlar sayfası. İki bölüm: "E-posta raporları" ve "Üyeler".
 *
 * Sayfanın tuttuğu tek kural: abonelik DOUBLE OPT-IN'dir (§26.3). Panelden eklenen
 * adres `confirmed=false` doğar ve rapor almaz; onay, e-postadaki tokenli linke
 * tıklanınca sunucu tarafında verilir. Bu yüzden liste ROZETLE ayrışır — "eklendi"
 * ile "rapor alıyor" aynı şey değildir ve kullanıcı bunu tek bakışta görmelidir.
 *
 * Görsel dil SEO/Isı Haritası sayfalarıyla birebir: beyaz başlık şeridi → beyaz
 * istatistik şeridi → #fafafa zeminde px-6 py-8 içerik.
 */

/** §26.3 — cadence seçenekleri; etiketler Türkçe, değerler şema kümesiyle aynı. */
const CADENCE_OPTIONS: { value: EmailCadence; label: string }[] = [
  { value: 'weekly', label: 'Haftalık (Pzt 08:00)' },
  { value: 'daily', label: 'Günlük (08:00)' },
];

/** Liste hücresinde kısa etiket — seçenek metninin (saat bilgili) uzun hâli sığmaz. */
const CADENCE_LABELS: Record<string, string> = {
  weekly: 'Haftalık',
  daily: 'Günlük',
};

/**
 * §26.6 hata sözleşmesi DURUM KODUNDAN okunur (mesaj metninden değil):
 * 400 geçersiz adres/cadence · 409 tavan aşımı · 503 `008_email.sql` uygulanmamış.
 * Sunucunun kendi Türkçe mesajı zaten açıklayıcıdır; yalnız 409'da tavanın NE OLDUĞUNU
 * anlatan cümle panelde de kurulur ki kullanıcı "neyi silmeliyim" sorusunu yanıtlayabilsin.
 */
function subscriptionErrorText(error: unknown): string | null {
  if (error === null || error === undefined) return null;
  if (error instanceof ApiError) {
    if (error.status === 409) {
      return `${error.message} — yeni abone eklemek için listeden birini silin.`;
    }
    return error.message;
  }
  return error instanceof Error ? error.message : String(error);
}

/**
 * §74.1/§74.3 — üyelik ve davet eylemlerinin hata sözleşmesi.
 *
 * `last_owner` (409) tek özel durumdur: sunucunun mesajı doğrudur ama kullanıcıya
 * NE YAPACAĞINI söylemez — önce başka birini owner yapması gerekir.
 */
function membershipErrorText(error: unknown): string | null {
  if (error === null || error === undefined) return null;
  if (error instanceof ApiError) {
    if (error.code === 'last_owner') {
      return `${error.message} — önce başka bir üyeyi sahip yapın.`;
    }
    if (error.status === 403) return 'Bu işlem için proje sahibi olmalısınız.';
    return error.message;
  }
  return error instanceof Error ? error.message : String(error);
}

/* -------------------------------------------------------------------------- */
/* §27.7 Üyeler                                                                */
/* -------------------------------------------------------------------------- */

/**
 * §27.7 — "Ayarlar → Üyeler: üye listesi (ad, e-posta, rol, son giriş), bekleyen
 * davetler, davet formu (e-posta + rol) — form YALNIZ owner'a görünür; viewer/editor
 * listeyi salt-okunur görür."
 *
 * Rol kararı `lib/auth.ts`'teki saf `canInviteMembers`'tan gelir; sunucu aynı kararı
 * §27.5'te tekrar verir (`POST /api/auth/invites` → owner). Formu gizlemek GÜVENLİK
 * DEĞİL, kesin 403 alacak bir formu göstermemek içindir.
 */
function MembersSection({ pid }: { pid: string }) {
  const queryClient = useQueryClient();
  const role = useRole(pid);
  const canInvite = canInviteMembers(role);

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<Role>('viewer');

  const members = useQuery({
    queryKey: qk.members(pid),
    queryFn: ({ signal }) => listMembers(pid, signal),
    retry: false,
  });

  const invites = useQuery({
    queryKey: qk.invites(pid),
    queryFn: ({ signal }) => listInvites(pid, signal),
    retry: false,
    // Davet listesi owner'ın işidir; viewer/editor için istek bile atılmaz (403 gürültüsü olmasın).
    enabled: canInvite,
  });

  const invite = useMutation({
    mutationFn: () => createInvite(pid, { email: inviteEmail.trim().toLowerCase(), role: inviteRole }),
    onSuccess: () => {
      // Adres temizlenir, rol KALIR — arka arkaya aynı rolde birkaç kişi davet etmek yaygın.
      setInviteEmail('');
      void queryClient.invalidateQueries({ queryKey: qk.invites(pid) });
    },
  });

  /* §74.1 — üye rolü / çıkarma; §74.3 — davet iptal / yeniden gönderme. */

  const rolDegistir = useMutation({
    mutationFn: (v: { userId: string; role: Role }) => setMemberRole(pid, v.userId, v.role),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.members(pid) });
      /*
       * `me` de tazelenir: kullanıcı KENDİ rolünü düşürdüyse panelin geri kalanı
       * (buton görünürlükleri, ray) yeni rolü hemen yansıtmalıdır — aksi hâlde
       * artık yapamayacağı işlerin butonlarını görmeye devam ederdi.
       */
      void queryClient.invalidateQueries({ queryKey: qk.me() });
    },
  });

  const cikar = useMutation({
    mutationFn: (userId: string) => removeMember(pid, userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.members(pid) });
      void queryClient.invalidateQueries({ queryKey: qk.me() });
    },
  });

  const iptal = useMutation({
    mutationFn: (id: number) => cancelInvite(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.invites(pid) });
    },
  });

  const yenidenGonder = useMutation({
    mutationFn: (id: number) => resendInvite(id),
    onSuccess: () => {
      // Yeni satır YENİ id taşır (eski silindi) — liste tazelenmeli.
      void queryClient.invalidateQueries({ queryKey: qk.invites(pid) });
    },
  });

  /** §74.1 — son owner koruması 409 `last_owner` ile gelir; mesaj sunucudan okunur. */
  const uyeHatasi = membershipErrorText(rolDegistir.error ?? cikar.error);
  const davetEylemHatasi = membershipErrorText(iptal.error ?? yenidenGonder.error);

  /*
   * Üye/davet listesi uçları §27.4 tablosunda yoktur (panel için eklenmiştir). Auth'u
   * henüz yayına almamış bir sunucu 404 döner: bu bir HATA DEĞİL "özellik yok"tur ve
   * kırmızı hata kutusu göstermek yanlış alarm olurdu.
   */
  const membersMissing = members.error instanceof ApiError && members.error.status === 404;
  const membersError =
    members.isError && !membersMissing ? (members.error as Error).message : null;

  const inviteError =
    invite.error instanceof ApiError
      ? invite.error.status === 403
        ? 'Davet göndermek için proje sahibi olmalısınız.'
        : invite.error.message
      : invite.error instanceof Error
        ? invite.error.message
        : null;

  const pendingInvites = invites.data ?? [];

  function submitInvite(event: FormEvent): void {
    event.preventDefault();
    if (inviteEmail.trim() === '' || invite.isPending) return;
    invite.mutate();
  }

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <Users className="h-4 w-4 text-[#9ca3af]" aria-hidden="true" />
          Üyeler
        </span>
      }
      subtitle="Projeye erişimi olan hesaplar. Yeni hesap YALNIZ davetle açılır (§27.7) — kayıt sayfası yoktur."
      actions={
        role !== null ? (
          <Badge tone={role === 'owner' ? 'accent' : 'neutral'} title={ROLE_DESCRIPTIONS[role]}>
            rolünüz: {ROLE_LABELS[role]}
          </Badge>
        ) : null
      }
    >
      {canInvite ? (
        <form data-testid="davet-formu" className="flex flex-wrap items-end gap-3" onSubmit={submitInvite}>
          <label className="flex min-w-[240px] flex-1 flex-col gap-1">
            <span className="text-[12px] text-[#6b7280]">E-posta adresi</span>
            <input
              data-testid="davet-eposta"
              type="email"
              value={inviteEmail}
              onChange={(event) => setInviteEmail(event.target.value)}
              placeholder="ad.soyad@sirket.com"
              maxLength={254}
              className="h-8 rounded-lg border border-[#e5e7eb] bg-white px-3 text-[13px] text-[#111827] outline-none placeholder:text-[#9ca3af] focus:border-[#d1d5db]"
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-[12px] text-[#6b7280]">Rol</span>
            <select
              data-testid="davet-rol"
              value={inviteRole}
              onChange={(event) => setInviteRole(event.target.value as Role)}
              className="h-8 rounded-lg border border-[#e5e7eb] bg-white px-2 text-[13px] text-[#111827] outline-none focus:border-[#d1d5db]"
            >
              {ROLES.map((option) => (
                <option key={option} value={option} title={ROLE_DESCRIPTIONS[option]}>
                  {ROLE_LABELS[option]}
                </option>
              ))}
            </select>
          </label>

          <button
            type="submit"
            data-testid="davet-gonder-btn"
            className="btn-primary"
            disabled={inviteEmail.trim() === '' || invite.isPending}
          >
            {invite.isPending ? 'Gönderiliyor…' : 'Davet gönder'}
          </button>
        </form>
      ) : (
        // §27.7 — viewer/editor listeyi SALT-OKUNUR görür; neden davet edemediği söylenir.
        <p data-testid="davet-yetkisi-yok" className="text-[12px] text-[#6b7280]">
          Üye listesi salt-okunurdur. Yeni üye davet etmek yalnız proje sahibinin (owner)
          yetkisindedir.
        </p>
      )}

      {inviteError !== null ? (
        <p className="mt-2 text-[12px] text-red-600">{inviteError}</p>
      ) : invite.isSuccess ? (
        <p className="mt-2 text-[12px] text-[#6b7280]">
          Davet e-postası kuyruğa alındı; bağlantı 7 gün geçerlidir (§27.2).
        </p>
      ) : null}

      {membersError !== null ? (
        <p className="mt-3 text-[12px] text-red-600">{membersError}</p>
      ) : null}
      {membersMissing ? (
        <p className="mt-3 text-[12px] text-[#9ca3af]">
          Bu sunucu sürümü üye listesi ucunu sunmuyor (auth henüz yayında değil).
        </p>
      ) : null}

      <div className="mt-5" data-testid="uyeler-listesi">
        <Table
          rows={members.data ?? []}
          rowKey={(row) => row.userId}
          loading={members.isLoading}
          emptyTitle="Üye yok"
          emptyDescription="Projede kayıtlı hesap görünmüyor. Davet formuyla ilk üyeyi ekleyin."
          columns={[
            {
              key: 'fullName',
              header: 'Ad soyad',
              render: (row) => (
                <span className="text-[#111827]">{row.fullName ?? '—'}</span>
              ),
            },
            {
              key: 'email',
              header: 'E-posta',
              render: (row) => (
                <span className="font-mono text-[12px] text-[#374151]">{row.email}</span>
              ),
            },
            {
              key: 'role',
              header: 'Rol',
              /*
               * §74.1 — owner için SEÇİCİ, diğerleri için ROZET.
               *
               * Tablo M39'a kadar tamamen salt-okunurdu: yanlış rolle davet edilmiş
               * bir üyeyi düzeltmenin ya da ayrılan birini çıkarmanın panelde hiçbir
               * yolu yoktu (org tarafında ikisi de vardı — asimetri bilinçli değildi).
               */
              render: (row) =>
                canInvite ? (
                  <select
                    data-testid="uye-rol"
                    data-uid={row.userId}
                    value={row.role}
                    disabled={rolDegistir.isPending}
                    onChange={(event) =>
                      rolDegistir.mutate({ userId: row.userId, role: event.target.value as Role })
                    }
                    className="h-8 rounded-lg border border-[#e5e7eb] bg-white px-2 text-[13px] outline-none"
                  >
                    {ROLES.map((option) => (
                      <option key={option} value={option} title={ROLE_DESCRIPTIONS[option]}>
                        {ROLE_LABELS[option]}
                      </option>
                    ))}
                  </select>
                ) : (
                  <Badge
                    tone={row.role === 'owner' ? 'accent' : row.role === 'editor' ? 'petrol' : 'neutral'}
                    title={ROLE_DESCRIPTIONS[row.role]}
                  >
                    {ROLE_LABELS[row.role]}
                  </Badge>
                ),
            },
            {
              key: 'lastLoginAt',
              header: 'Son giriş',
              cellClassName: 'hidden md:table-cell',
              render: (row) => (
                // Hiç giriş yapmamış üyeye tarih UYDURULMAZ — '—' gerçeği söyler.
                <span className="num text-[12px] text-[#9ca3af]">
                  {row.lastLoginAt === null ? '—' : formatDateTime(row.lastLoginAt)}
                </span>
              ),
            },
            ...(canInvite
              ? [
                  {
                    key: 'cikar',
                    header: '',
                    align: 'right' as const,
                    render: (row: MemberRow) => (
                      <button
                        type="button"
                        data-testid="uye-cikar"
                        data-uid={row.userId}
                        aria-label={`${row.email} üyeliğini kaldır`}
                        className="btn-ghost"
                        disabled={cikar.isPending}
                        onClick={() => cikar.mutate(row.userId)}
                      >
                        <UserMinus className="h-3.5 w-3.5" aria-hidden="true" />
                        Çıkar
                      </button>
                    ),
                  },
                ]
              : []),
          ]}
        />
        {uyeHatasi !== null ? (
          <p data-testid="uye-hata" role="alert" className="mt-2 text-[12px] text-red-600">
            {uyeHatasi}
          </p>
        ) : null}
      </div>

      {canInvite ? (
        <div className="mt-6" data-testid="bekleyen-davetler">
          <p className="mb-2 text-[12px] font-semibold text-[#6b7280]">Bekleyen davetler</p>
          <Table
            rows={pendingInvites}
            rowKey={(row) => row.id}
            loading={invites.isLoading}
            emptyTitle="Bekleyen davet yok"
            emptyDescription="Gönderilen davetler kabul edilene ya da süresi dolana kadar burada görünür."
            columns={[
              {
                key: 'email',
                header: 'E-posta',
                render: (row) => (
                  <span className="font-mono text-[12px] text-[#374151]">{row.email}</span>
                ),
              },
              {
                key: 'role',
                header: 'Rol',
                render: (row) => <Badge tone="outline">{ROLE_LABELS[row.role]}</Badge>,
              },
              {
                key: 'expiresAt',
                header: 'Geçerlilik',
                render: (row) =>
                  /*
                   * §27.2 — davet 7 gün geçerlidir. Süresi geçmiş satır listede kalabilir
                   * (sunucu temizlemeden); rozet bunu açıkça söyler ki owner yeni davet
                   * göndermesi gerektiğini anlasın.
                   */
                  isInviteExpired(row) ? (
                    <Badge tone="danger" dot title="Bu davet artık kabul edilemez">
                      süresi doldu
                    </Badge>
                  ) : (
                    <span className="num text-[12px] text-[#9ca3af]">
                      {formatDateTime(row.expiresAt)}
                    </span>
                  ),
              },
              {
                key: 'eylem',
                header: '',
                align: 'right',
                /*
                 * §74.3 — M39'a kadar bu satırda HİÇBİR eylem yoktu: "süresi doldu"
                 * rozetini gören owner ne iptal edebiliyor ne yeniden gönderebiliyordu.
                 * Çıkışsız bir boş durumun tablo hâliydi.
                 */
                render: (row) => (
                  <span className="flex justify-end gap-1">
                    <button
                      type="button"
                      data-testid="davet-yeniden"
                      data-id={row.id}
                      className="btn-ghost"
                      disabled={yenidenGonder.isPending}
                      onClick={() => yenidenGonder.mutate(row.id)}
                      title="Yeni bir bağlantı üretir; eskisi geçersiz olur"
                    >
                      <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
                      Yeniden gönder
                    </button>
                    <button
                      type="button"
                      data-testid="davet-iptal"
                      data-id={row.id}
                      className="btn-ghost"
                      disabled={iptal.isPending}
                      onClick={() => iptal.mutate(row.id)}
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                      İptal
                    </button>
                  </span>
                ),
              },
            ]}
          />
          {davetEylemHatasi !== null ? (
            <p data-testid="davet-eylem-hata" role="alert" className="mt-2 text-[12px] text-red-600">
              {davetEylemHatasi}
            </p>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* §13/§72.3 — Site ve kurulum kartı                                           */
/* -------------------------------------------------------------------------- */

/**
 * §13/§72.3 — "hangi siteyi izliyorum, etiketim ne, çalışıyor mu".
 *
 * NEDEN AYRI KART ve NEDEN HER ROLE ÇİZİLİR: bu üç bilgi M37'ye kadar YALNIZ owner'a
 * açık `ProjectSection` formunun içindeydi ve site adresi orada bir input'un
 * PLACEHOLDER'ıydı — yani "kayıtlı alan adı" ile "örnek metin" aynı gri tonda
 * görünüyordu. Viewer/editor ise izlenen sitenin adresini panelin HİÇBİR yerinde
 * göremiyordu. Oysa "hangi siteyi izliyoruz" bir yapılandırma YETKİSİ değil, projenin
 * kimliğidir; okumak için owner olmak gerekmez (uç de `viewer`a açıktır).
 *
 * Kartın ikinci işi TEŞHİStir: `useIngestStatus` "olay geliyor mu" ile "oturum
 * kapandı mı"yı ayırır, böylece etiketi kurmuş kullanıcı "SDK'yı ekleyin" metnini
 * görüp kurulumunu bozuk sanmaz (bkz. components/KurulumDurumu.tsx).
 */
function SiteKurulumSection({ pid }: { pid: string }) {
  const { projects } = useProjectContext();
  const project = projects.find((p) => p.id === pid);
  const { status, loading, verdict } = useIngestStatus(pid);
  const [kopyalandi, setKopyalandi] = useState<'pid' | 'snippet' | null>(null);
  // Alan adını KİM ekleyebilir: aşağıdaki "Proje" kartı yalnız owner'a çizilir (§72.2).
  const owner = useRole(pid) === 'owner';

  /*
   * Alan adı iki kaynaktan gelebilir: proje listesi (ray/seçici zaten taşır) ve
   * kurulum durumu yanıtı. Sunucudan geleni ÖNCELERİZ — liste önbellekten gelmiş,
   * ayar değişikliğinden sonra tazelenmemiş olabilir.
   */
  const domain = status?.domain ?? project?.domain ?? null;
  const siteUrl = domain === null || domain === '' ? null : `https://${domain.replace(/^https?:\/\//i, '')}`;
  const snippet = sdkSnippet(pid);

  async function kopyala(what: 'pid' | 'snippet', text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      setKopyalandi(what);
    } catch {
      setKopyalandi(null);
    }
  }

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <Globe className="h-4 w-4 text-[#9ca3af]" aria-hidden="true" />
          Site ve kurulum
        </span>
      }
      subtitle="İzlenen site adresi, proje kimliği ve izleyici etiketi. Etiketin gerçekten veri gönderip göndermediği burada görünür."
      actions={
        loading ? (
          <span className="animate-pulse text-[12px] text-[#9ca3af]">Kontrol ediliyor…</span>
        ) : (
          /* `Badge` yalnız bildiği prop'ları çizer (data-* geçirmez) — testId sarmalayıcıda. */
          <span data-testid="kurulum-rozet">
            <Badge tone={kurulumTone(verdict.kind)} dot>
              {verdict.label}
            </Badge>
          </span>
        )
      }
    >
      <div className="space-y-5" data-testid="site-kurulum-karti">
        <dl className="grid gap-4 sm:grid-cols-2">
          <div>
            <dt className="text-[12px] text-[#6b7280]">Site adresi</dt>
            <dd className="mt-1 text-[13px]">
              {siteUrl === null ? (
                /* Alan adı BOŞ da bir gerçektir; 'ornek.com' yazmak kayıtlı değermiş gibi görünürdü. */
                <span data-testid="site-url-yok" className="text-[#9ca3af]">
                  {owner
                    ? 'Tanımlı değil — aşağıdaki “Proje” kartından ekleyin.'
                    : 'Tanımlı değil — proje sahibi ekleyebilir.'}
                </span>
              ) : (
                <a
                  data-testid="site-url"
                  href={siteUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="font-medium text-brand hover:underline"
                >
                  {domain}
                </a>
              )}
            </dd>
            <p className="mt-0.5 text-[11px] text-[#9ca3af]">
              Isı haritasının arka planı ve SEO taramasının hedefi bu adrestir.
            </p>
          </div>

          <div>
            <dt className="text-[12px] text-[#6b7280]">Proje kimliği (pid)</dt>
            <dd className="mt-1 flex items-center gap-2">
              <code
                data-testid="proje-pid"
                className="rounded bg-[#f3f4f6] px-1.5 py-0.5 font-mono text-[12px] text-[#111827]"
              >
                {pid}
              </code>
              <button
                type="button"
                className="btn-ghost"
                onClick={() => void kopyala('pid', pid)}
                aria-label="Proje kimliğini kopyala"
              >
                <Copy className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
              {kopyalandi === 'pid' ? (
                <span className="text-[12px] text-emerald-600">Kopyalandı.</span>
              ) : null}
            </dd>
            <p className="mt-0.5 text-[11px] text-[#9ca3af]">
              Etiketteki <code className="rounded bg-[#f3f4f6] px-1">data-pid</code> bununla
              birebir aynı olmalıdır; farklı pid taşıyan istek sessizce atılır.
            </p>
          </div>
        </dl>

        {/* §72.3 — SDK etiketi: sihirbaz, boş durum ve bu kart AYNI kaynaktan üretir. */}
        <div>
          <p className="text-[12px] font-medium text-[#374151]">İzleyici etiketi</p>
          <pre
            data-testid="kurulum-snippet-ayarlar"
            className="mt-1.5 max-w-full overflow-x-auto rounded-xl bg-[#171719] px-4 py-3 text-left font-mono text-[12px] text-[#e5e7eb]"
          >
            {snippet}
          </pre>
          <div className="mt-2 flex items-center gap-3">
            <button type="button" className="btn-ghost" onClick={() => void kopyala('snippet', snippet)}>
              <Copy className="mr-1.5 inline h-3.5 w-3.5" aria-hidden="true" />
              Etiketi kopyala
            </button>
            {kopyalandi === 'snippet' ? (
              <span className="text-[12px] text-emerald-600">Kopyalandı.</span>
            ) : null}
          </div>
        </div>

        {/* Teşhis — durumun NE ANLAMA geldiği ve ne yapılacağı. */}
        <div
          data-testid="kurulum-teshis"
          className={`rounded-xl border px-4 py-3 ${
            verdict.receiving
              ? 'border-[#e5e7eb] bg-[#fafafa]'
              : 'border-[#fde68a] bg-[#fffbeb]'
          }`}
        >
          <p className="text-[12px] text-[#374151]">{verdict.description}</p>

          {status !== null ? (
            <dl className="mt-3 flex flex-wrap items-center gap-x-8 gap-y-2">
              {[
                {
                  label: 'Son olay',
                  value: status.lastEventAt === null ? 'yok' : formatAgo(status.lastEventAt),
                  testId: 'kurulum-son-olay',
                },
                {
                  label: 'Son 24s olay',
                  value:
                    status.botEvents24h > 0
                      ? `${formatNumber(status.events24h)} (${formatNumber(status.botEvents24h)} bot)`
                      : formatNumber(status.events24h),
                  testId: 'kurulum-olay-24s',
                },
                {
                  label: 'Son kapanan oturum',
                  value: status.lastSessionAt === null ? 'yok' : formatAgo(status.lastSessionAt),
                  testId: 'kurulum-son-oturum',
                },
                {
                  label: 'Son 24s oturum',
                  value: formatNumber(status.sessions24h),
                  testId: 'kurulum-oturum-24s',
                },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-2">
                  <dt className="text-[12px] text-[#9ca3af]">{item.label}</dt>
                  <dd data-testid={item.testId} className="num text-[13px] font-semibold text-[#111827]">
                    {item.value}
                  </dd>
                </div>
              ))}
            </dl>
          ) : null}
        </div>
      </div>
    </Card>
  );
}

/** §19.1 — maliyet profili etiketleri; API değerleri (eco/balanced/performance) İNGİLİZCE kalır. */
const COST_PROFILE_LABELS: Record<string, string> = {
  eco: 'Ekonomik',
  balanced: 'Dengeli',
  performance: 'Performans',
};


/* -------------------------------------------------------------------------- */
/* §85.5 — Veri hakları kartı (KVKK/GDPR)                                      */
/* -------------------------------------------------------------------------- */

/**
 * §85.5 — ziyaretçi verisi silme ve taşınabilirlik.
 *
 * METİN ROL AYRIMINI SÖYLER (§85.1) ve bu ekranın en önemli parçasıdır: DXP veri
 * İŞLEYENDİR, müşteri SORUMLUDUR. `aid` anonimdir ve DXP onu bir insana bağlayamaz
 * (PII sunucuya hiç ulaşmaz) — ziyaretçiyi tanımak müşterinin işidir. Bunu
 * yazmamak, müşterinin kendi yükümlülüğünü DXP'de sanmasına yol açardı.
 */
function DataRightsSection({ pid }: { pid: string }) {
  const queryClient = useQueryClient();
  const [aid, setAid] = useState('');
  const [onay, setOnay] = useState(false);

  const talepler = useQuery({
    queryKey: qk.erasure(pid),
    queryFn: ({ signal }) => getErasureRequests(pid, signal),
    retry: false,
  });

  const sil = useMutation({
    mutationFn: () => eraseVisitor(pid, aid.trim()),
    onSuccess: async () => {
      setAid('');
      setOnay(false);
      await queryClient.invalidateQueries({ queryKey: qk.erasure(pid) });
    },
  });

  const rows = talepler.data?.requests ?? [];
  const hata = sil.error;

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-[#9ca3af]" aria-hidden="true" />
          Veri hakları (KVKK/GDPR)
        </span>
      }
      subtitle="Ziyaretçinizden gelen silme veya veri taşıma talebini burada karşılarsınız."
    >
      <div className="space-y-4" data-testid="veri-haklari-karti">
        <p className="rounded-xl border border-[#e5e7eb] bg-[#fafafa] px-4 py-3 text-[12px] text-[#6b7280]">
          <strong className="text-[#374151]">Ziyaretçiyi tanımak size aittir.</strong> DXP
          ziyaretçileri anonim bir kimlikle (<code className="rounded bg-[#f3f4f6] px-1">aid</code>)
          izler ve onu bir kişiye <strong>bağlayamaz</strong> — kişisel veri (e-posta, ad,
          kart) sunucuya hiç ulaşmaz. Talebi kendi sitenizde karşılayıp ilgili{' '}
          <code className="rounded bg-[#f3f4f6] px-1">aid</code> değerini buraya girin.
        </p>

        <form
          className="flex flex-wrap items-end gap-3"
          data-testid="silme-formu"
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            if (aid.trim() !== '') setOnay(true);
          }}
        >
          <label className="flex min-w-[240px] flex-1 flex-col gap-1">
            <span className="text-[12px] text-[#6b7280]">Ziyaretçi kimliği (aid)</span>
            <input
              data-testid="silme-aid"
              type="text"
              value={aid}
              maxLength={64}
              placeholder="a1b2c3d4e5f6"
              onChange={(event) => {
                setAid(event.target.value);
                setOnay(false);
              }}
              className="h-8 rounded-lg border border-[#e5e7eb] bg-white px-3 font-mono text-[12px] text-[#111827] outline-none placeholder:text-[#9ca3af] focus:border-[#d1d5db]"
            />
          </label>

          {/* §85.3 — taşınabilirlik: silmeden ÖNCE veriyi indirmek isteyebilir. */}
          <a
            data-testid="silme-disa-aktar"
            href={aid.trim() === '' ? undefined : erasureExportUrl(pid, aid.trim())}
            className={`btn-ghost ${aid.trim() === '' ? 'pointer-events-none opacity-40' : ''}`}
            download
          >
            <Download className="mr-1.5 inline h-3.5 w-3.5" aria-hidden="true" />
            Verisini indir
          </a>

          {onay ? (
            <span className="flex items-center gap-2">
              <span className="text-[12px] text-[#991b1b]">Geri alınamaz. Emin misiniz?</span>
              <button
                type="button"
                data-testid="silme-onayla"
                className="btn-primary"
                disabled={sil.isPending}
                onClick={() => sil.mutate()}
              >
                {sil.isPending ? 'Siliniyor…' : 'Evet, sil'}
              </button>
              <button type="button" className="btn-ghost" onClick={() => setOnay(false)}>
                Vazgeç
              </button>
            </span>
          ) : (
            <button
              type="submit"
              data-testid="silme-baslat"
              className="btn-ghost"
              disabled={aid.trim() === ''}
            >
              <Trash2 className="mr-1.5 inline h-3.5 w-3.5" aria-hidden="true" />
              Verisini sil
            </button>
          )}
        </form>

        {hata !== null && hata !== undefined ? (
          <p data-testid="silme-hata" role="alert" className="text-[12px] text-red-600">
            {hata instanceof Error ? hata.message : String(hata)}
          </p>
        ) : null}

        {/*
          §85.4 — TALEP LİSTESİ KANITTIR. Denetimde sorulan soru "sildiniz mi" değil,
          "sildiğinizi gösterebiliyor musunuz"dur; mutasyon bittiğinde ClickHouse
          tarafında hiçbir iz kalmaz.
        */}
        <div>
          <p className="mb-2 text-[12px] font-semibold text-[#6b7280]">Son silme talepleri</p>
          {rows.length === 0 ? (
            <p className="text-[12px] text-[#9ca3af]">
              Henüz talep yok. Karşıladığınız her talep burada kayda geçer ve denetimde
              kanıt olarak kullanılabilir.
            </p>
          ) : (
            <table className="w-full text-[13px]" data-testid="silme-talepleri">
              <thead>
                <tr className="text-left text-[12px] text-[#9ca3af]">
                  <th className="py-1 pr-4">Konu</th>
                  <th className="py-1 pr-4">Durum</th>
                  <th className="py-1 pr-4">Silinen satır</th>
                  <th className="py-1">Tarih</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-[#f3f4f6]">
                    <td className="py-1.5 pr-4 font-mono text-[12px] text-[#374151]">{r.subject}</td>
                    <td className="py-1.5 pr-4">
                      <Badge
                        tone={r.status === 'done' ? 'petrol' : r.status === 'failed' ? 'danger' : 'neutral'}
                        size="sm"
                      >
                        {r.status}
                      </Badge>
                    </td>
                    <td className="num py-1.5 pr-4 text-[12px] text-[#6b7280]">
                      {/* null = henüz ölçülmedi, 0 = ölçüldü ve satır yoktu; ayrım korunur. */}
                      {r.rowsDeleted === null ? '—' : formatNumber(r.rowsDeleted)}
                    </td>
                    <td className="py-1.5 text-[12px] text-[#9ca3af]">
                      {formatDateTime(r.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* §72.2/3/4 — Proje kartı                                                     */
/* -------------------------------------------------------------------------- */

/**
 * §72 — projenin KENDİSİ: ad, domain, maliyet profili, SDK etiketi, anahtar
 * döndürme, arşiv.
 *
 * NEDEN Ayarlar sayfasında: bu sayfa zaten PROJE yapılandırmasıdır (org'unki
 * `/organizasyon`tadır, §13.2 ray sırası). M37'ye kadar burada projenin kendisine
 * dair TEK bir alan yoktu — kullanıcı adını değiştiremiyor, domain'ini
 * düzeltemiyor, SDK etiketini bir daha göremiyordu (yalnız kurulum sihirbazında,
 * bir kez).
 *
 * YALNIZ proje `owner`ına çizilir: dört ucun dördü de owner ister (RBAC tablosu).
 */
function ProjectSection({ pid }: { pid: string }) {
  const queryClient = useQueryClient();
  const { projects, orgId } = useProjectContext();
  const project = projects.find((p) => p.id === pid);
  const arsivli = (project?.archived_at ?? null) !== null;

  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [profile, setProfile] = useState<CostProfile | ''>('');
  const [yeniAnahtar, setYeniAnahtar] = useState<string | null>(null);
  const [onay, setOnay] = useState<'rotate' | 'archive' | null>(null);

  /*
   * Form alanları bağlamdan DOLDURULMAZ, yalnız BOŞKEN bağlam değeri gösterilir
   * (`value={name || project.name}` yerine placeholder deseni): kullanıcı yazarken
   * arka planda tazelenen bir sorgu yazdığını EZMEMELİ. Yazılmamış alan
   * gönderilmez — sunucu da "verilmeyen alan değişmez" sözleşmesini uygular (§72.2).
   */
  const kaydet = useMutation({
    mutationFn: () => {
      const patch: { name?: string; domain?: string; cost_profile?: CostProfile } = {};
      if (name.trim() !== '') patch.name = name.trim();
      if (domain !== '') patch.domain = domain.trim();
      if (profile !== '') patch.cost_profile = profile;
      return updateProjectSettings(pid, patch);
    },
    onSuccess: async () => {
      setName('');
      setProfile('');
      await queryClient.invalidateQueries({ queryKey: qk.orgProjects(orgId ?? '') });
      await queryClient.invalidateQueries({ queryKey: qk.costs(pid) });
    },
  });

  const rotate = useMutation({
    mutationFn: () => rotateProjectKey(pid),
    onSuccess: (data) => {
      /*
       * §71.2 KARAR 2 — anahtar YALNIZ bu yanıtta gelir; bileşen durumunda tutulur
       * ve sayfa yenilenince kaybolur. Bunu bir sorguya yazmak (önbelleğe koymak)
       * sırrı panelin belleğinde süresiz bırakırdı.
       */
      setYeniAnahtar(data.project.api_key);
      setOnay(null);
    },
  });

  const arsiv = useMutation({
    mutationFn: (arsivle: boolean) => setProjectArchived(pid, arsivle),
    onSuccess: async () => {
      setOnay(null);
      await queryClient.invalidateQueries({ queryKey: qk.orgProjects(orgId ?? '') });
    },
  });

  const hata = kaydet.error ?? rotate.error ?? arsiv.error;

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <Globe className="h-4 w-4 text-[#9ca3af]" aria-hidden="true" />
          Proje
        </span>
      }
      subtitle="Ad, alan adı ve maliyet profili. Proje kimliği (pid) DEĞİŞTİRİLEMEZ: SDK etiketiniz ve tüm geçmiş veri ona bağlıdır."
      actions={
        arsivli ? (
          <Badge tone="warning" title="§72.4 — veri toplanmıyor, geçmiş okunabilir">
            arşivli
          </Badge>
        ) : null
      }
    >
      <div className="space-y-5" data-testid="proje-karti">
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            if (!kaydet.isPending) kaydet.mutate();
          }}
        >
          <label className="flex min-w-[180px] flex-1 flex-col gap-1">
            <span className="text-[12px] text-[#6b7280]">Proje adı</span>
            <input
              data-testid="proje-ad"
              type="text"
              value={name}
              placeholder={project?.name ?? ''}
              maxLength={120}
              onChange={(event) => setName(event.target.value)}
              className="h-8 rounded-lg border border-[#e5e7eb] bg-white px-3 text-[13px] text-[#111827] outline-none placeholder:text-[#9ca3af] focus:border-[#d1d5db]"
            />
          </label>

          <label className="flex min-w-[180px] flex-1 flex-col gap-1">
            <span className="text-[12px] text-[#6b7280]">Alan adı (şemasız)</span>
            <input
              data-testid="proje-domain"
              type="text"
              value={domain}
              placeholder={project?.domain ?? 'ornek.com'}
              maxLength={253}
              onChange={(event) => setDomain(event.target.value)}
              className="h-8 rounded-lg border border-[#e5e7eb] bg-white px-3 text-[13px] text-[#111827] outline-none placeholder:text-[#9ca3af] focus:border-[#d1d5db]"
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-[12px] text-[#6b7280]">Maliyet profili</span>
            <select
              data-testid="proje-profil"
              value={profile}
              onChange={(event) => setProfile(event.target.value as CostProfile | '')}
              className="h-8 rounded-lg border border-[#e5e7eb] bg-white px-2 text-[13px] text-[#111827] outline-none focus:border-[#d1d5db]"
            >
              <option value="">Değiştirme</option>
              {(['eco', 'balanced', 'performance'] as const).map((value) => (
                <option key={value} value={value}>
                  {COST_PROFILE_LABELS[value]}
                </option>
              ))}
            </select>
          </label>

          <button
            type="submit"
            data-testid="proje-kaydet"
            className="btn-primary"
            disabled={kaydet.isPending || (name.trim() === '' && domain === '' && profile === '')}
          >
            {kaydet.isPending ? 'Kaydediliyor…' : 'Kaydet'}
          </button>
        </form>

        <p className="text-[12px] text-[#9ca3af]">
          Alan adı ısı haritasının arka planını ve SEO taramasının hedefini belirler.
          Değişiklik veri akışına en geç bir dakikada yansır.
        </p>

        {/*
          §72.3 SDK etiketi BURADA DEĞİL, sayfanın en üstündeki "Site ve kurulum"
          kartındadır: etiket owner'a özel bir ayar değil, projenin herkese açık
          kimliğidir ve iki kez çizmek "hangisi güncel" sorusunu doğururdu.
        */}

        {/* §72.3 — anahtar döndürme */}
        <div className="rounded-xl border border-[#e5e7eb] bg-[#fafafa] px-4 py-3">
          <p className="text-[12px] font-medium text-[#374151]">Proje anahtarı</p>
          <p className="mt-0.5 text-[12px] text-[#9ca3af]">
            Anahtar bir daha GÖSTERİLMEZ; kaybolduysa döndürün. Döndürme eski anahtarı{' '}
            <strong>anında</strong> geçersiz kılar — sunucu-sunucu entegrasyonlarınız o
            an durur.
          </p>

          {yeniAnahtar !== null ? (
            <div className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
              <p className="text-[12px] font-medium text-emerald-800">
                Yeni anahtar — yalnız şimdi gösterilir
              </p>
              <p data-testid="yeni-anahtar" className="mt-1 break-all font-mono text-[12px] text-[#111827]">
                {yeniAnahtar}
              </p>
            </div>
          ) : onay === 'rotate' ? (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className="text-[12px] text-[#374151]">Emin misiniz?</span>
              <button
                type="button"
                data-testid="anahtar-dondur-onayla"
                className="btn-primary"
                disabled={rotate.isPending}
                onClick={() => rotate.mutate()}
              >
                {rotate.isPending ? 'Döndürülüyor…' : 'Evet, döndür'}
              </button>
              <button type="button" className="btn-ghost" onClick={() => setOnay(null)}>
                Vazgeç
              </button>
            </div>
          ) : (
            <button
              type="button"
              data-testid="anahtar-dondur"
              className="btn-ghost mt-2"
              onClick={() => setOnay('rotate')}
            >
              <KeyRound className="mr-1.5 inline h-3.5 w-3.5" aria-hidden="true" />
              Anahtarı döndür
            </button>
          )}
        </div>

        {/* §72.4 — tehlikeli bölge: ARŞİV (silme fiili yoktur) */}
        <div className="rounded-xl border border-[#fecaca] bg-[#fef2f2] px-4 py-3">
          <p className="text-[12px] font-medium text-[#991b1b]">Tehlikeli bölge</p>
          <p className="mt-0.5 text-[12px] text-[#b91c1c]">
            {arsivli
              ? 'Bu proje arşivli: yeni veri kabul edilmiyor, geçmiş veriler duruyor ve okunabiliyor.'
              : 'Arşivlemek veri toplamayı durdurur; TOPLANMIŞ veri silinmez ve panelde okunmaya devam eder. Projeyi silmenin bir yolu yoktur — bu bilinçlidir.'}
          </p>

          {arsivli ? (
            <button
              type="button"
              data-testid="proje-arsivden-cikar"
              className="btn-ghost mt-2"
              disabled={arsiv.isPending}
              onClick={() => arsiv.mutate(false)}
            >
              <ArchiveRestore className="mr-1.5 inline h-3.5 w-3.5" aria-hidden="true" />
              {arsiv.isPending ? 'Geri alınıyor…' : 'Arşivden çıkar'}
            </button>
          ) : onay === 'archive' ? (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className="text-[12px] text-[#991b1b]">
                Veri akışı duracak. Emin misiniz?
              </span>
              <button
                type="button"
                data-testid="proje-arsivle-onayla"
                className="btn-primary"
                disabled={arsiv.isPending}
                onClick={() => arsiv.mutate(true)}
              >
                {arsiv.isPending ? 'Arşivleniyor…' : 'Evet, arşivle'}
              </button>
              <button type="button" className="btn-ghost" onClick={() => setOnay(null)}>
                Vazgeç
              </button>
            </div>
          ) : (
            <button
              type="button"
              data-testid="proje-arsivle"
              className="btn-ghost mt-2"
              onClick={() => setOnay('archive')}
            >
              <Archive className="mr-1.5 inline h-3.5 w-3.5" aria-hidden="true" />
              Projeyi arşivle
            </button>
          )}
        </div>

        {hata !== null && hata !== undefined ? (
          <p data-testid="proje-hata" role="alert" className="text-[12px] text-red-600">
            {hata instanceof Error ? hata.message : String(hata)}
          </p>
        ) : null}
      </div>
    </Card>
  );
}

/**
 * §19.6 — MÜŞTERİ BAŞINA MALİYET (COGS) ve brüt marj: "kârlılığın görünürlüğü".
 *
 * NEDEN AYARLAR SAYFASINDA ve NEDEN SADECE SAHİBE: uç (`GET /projects/:pid/costs`)
 * RBAC tablosunda YAZILI DEĞİLDİR, yani fail-closed kuralıyla `owner` ister — ve
 * `ADMIN_TOKEN` örtük owner'dır (§27.6). Kart bu kapının panel tarafındaki aynasıdır;
 * viewer/editor'e maliyet göstermek, sunucunun zaten 403 döneceği bir kutu çizmek olurdu.
 *
 * Bu kart §19.6'nın son halkasıydı: `project_costs`, `cost-meter` job'ı, uç ve
 * `api.ts` istemcisi vardı ama hiçbir sayfa çağırmıyordu — ölçülen maliyet
 * kimseye görünmüyordu.
 */
function CostSection({ pid }: { pid: string }) {
  const costs = useQuery({
    queryKey: qk.costs(pid),
    queryFn: ({ signal }) => getCosts(pid, signal),
    // Maliyet GÜNLÜK bir büyüklüktür (cost-meter gecelik koşar); sık yenilemek
    // ağır sorguyu boşuna tekrarlar.
    staleTime: 10 * 60_000,
    retry: false,
  });

  if (costs.isError) {
    return (
      <ErrorState
        title="Maliyet özeti alınamadı"
        description={
          costs.error instanceof ApiError && costs.error.status === 403
            ? 'Bu kartı yalnızca proje sahibi görebilir (§19.6).'
            : 'cost-meter job’ı henüz koşmamış olabilir.'
        }
      />
    );
  }

  const data = costs.data;
  if (data === undefined) return null;

  const olculdu = data.daily.length > 0;
  const gelirVar = data.planRevenueTl > 0;

  return (
    <Card
      title="Maliyet ve marj"
      subtitle="Son 30 günün ölçülen işletme maliyeti (§19.6). Değerler cost-meter job’ının yazdığı gerçek kullanımdan gelir; tahmin değildir."
      actions={
        <Badge tone="neutral" title="§19.1 — profil örnekleme ve job sıklıklarını belirler">
          profil: {COST_PROFILE_LABELS[data.costProfile] ?? data.costProfile}
        </Badge>
      }
    >
      {!olculdu ? (
        /* Sıfır UYDURMA: ölçüm yoksa "0 TL maliyet" demek, ölçülmemişi ölçülmüş gibi
           gösterirdi. §19.6'nın tüm değeri sayının GERÇEK olmasındadır. */
        <p className="text-[13px] text-[#6b7280]">
          Henüz ölçüm yok — <code className="rounded bg-[#f3f4f6] px-1">pnpm cost-meter</code>{' '}
          koştuğunda bu kart dolar.
        </p>
      ) : (
        <>
          <dl className="grid gap-4 sm:grid-cols-3">
            <div>
              <dt className="text-[11px] font-medium uppercase tracking-wide text-[#6b7280]">
                Aylık COGS
              </dt>
              <dd className="mt-1 text-[20px] font-semibold text-[#111827]">
                {formatTL(data.monthlyCostTl)}
              </dd>
              <p className="mt-0.5 text-[12px] text-[#6b7280]">
                {formatNumber(data.monthlyEvents)} olay
              </p>
            </div>

            <div>
              <dt className="text-[11px] font-medium uppercase tracking-wide text-[#6b7280]">
                Plan geliri
              </dt>
              <dd className="mt-1 text-[20px] font-semibold text-[#111827]">
                {gelirVar ? formatTL(data.planRevenueTl) : '—'}
              </dd>
              <p className="mt-0.5 text-[12px] text-[#6b7280]">
                {gelirVar ? 'projects.plan_revenue_tl_month' : 'plan geliri girilmemiş'}
              </p>
            </div>

            <div>
              <dt className="text-[11px] font-medium uppercase tracking-wide text-[#6b7280]">
                Brüt marj
              </dt>
              <dd
                className={`mt-1 text-[20px] font-semibold ${
                  !gelirVar ? 'text-[#111827]' : data.marginWarning ? 'text-red-600' : 'text-emerald-700'
                }`}
              >
                {/* Gelir yoksa marj HESAPLANAMAZ; %0 yazmak "zarar ediyoruz" derdi. */}
                {gelirVar ? `%${formatNumber(data.grossMarginPct, 1)}` : '—'}
              </dd>
              <p className="mt-0.5 text-[12px] text-[#6b7280]">
                {gelirVar ? 'eşik: %70' : 'gelir girilmeden hesaplanamaz'}
              </p>
            </div>
          </dl>

          {data.marginWarning ? (
            <p className="mt-4 rounded-md bg-[#fef2f2] px-3 py-2 text-[13px] text-red-700">
              <strong>Marj eşiğin altında.</strong> Bu müşteri <em>eco</em> profiline alınmalı ya da
              üst pakete geçmeli (§19.6).
            </p>
          ) : null}
        </>
      )}
    </Card>
  );
}

export default function Settings() {
  const pid = useActivePid();
  const queryClient = useQueryClient();
  // §19.6 — maliyet kartının kapısı; uç zaten owner ister (fail-closed RBAC).
  const role = useRole(pid);

  const [email, setEmail] = useState('');
  const [cadence, setCadence] = useState<EmailCadence>('weekly');

  /*
   * §26.6 — abonelik uçlarının HEPSİ (GET dahil) owner ister; RBAC tablosu
   * `/projects/:pid/subscriptions*` için `requirement: 'owner'` der.
   *
   * Sorgu koşulsuz koşuyordu: viewer/editor sayfayı açtığında kesin 403 alıyor,
   * ekranda "Abone listesi alınamadı" hatası çıkıyordu — oysa hata yok, YETKİ yok
   * (denetim bulgusu B8). Kapı, diğer owner kartlarıyla (proje, veri hakları,
   * maliyet) aynı desende kurulur.
   */
  const subscriptions = useQuery({
    queryKey: qk.subscriptions(pid),
    queryFn: ({ signal }) => listSubscriptions(pid, signal),
    enabled: role === 'owner',
  });

  const rows: EmailSubscription[] = subscriptions.data ?? [];

  const add = useMutation({
    mutationFn: () => addSubscription(pid, { email: email.trim(), cadence }),
    onSuccess: () => {
      // Adres alanı temizlenir; cadence KALIR — arka arkaya birkaç kişi eklemek yaygın.
      setEmail('');
      void queryClient.invalidateQueries({ queryKey: qk.subscriptions(pid) });
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => deleteSubscription(pid, id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.subscriptions(pid) });
    },
  });

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (email.trim() === '' || add.isPending) return;
    add.mutate();
  }

  const addError = subscriptionErrorText(add.error);
  const removeError = subscriptionErrorText(remove.error);
  const pending = rows.filter((row) => !row.confirmed).length;

  return (
    <div className="min-h-full bg-[#fafafa]" data-testid="ayarlar-sayfasi">
      {/* başlık */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#e5e7eb] bg-white px-6 py-4">
        <div className="flex items-center gap-2">
          <h1 className="font-grotesk text-[18px] font-bold text-[#111827]">Ayarlar</h1>
          <BilgiDugmesi testId="ayarlar-bilgi">E-posta raporu, paneli hiç açmayan paydaşa gönderilen özettir (§26.1): 5 KPI, en önemli 3 bulgu, funnel'ın en kötü adımı ve varsa Atlas özeti. İçerik yalnız üretilmiş veriden derlenir.</BilgiDugmesi>
          <span className="ml-1 hidden text-[13px] text-[#9ca3af] md:inline">
            E-posta raporu aboneleri · çift onaylı (double opt-in) · proje üyeleri (§27.7)
          </span>
        </div>
      </div>

      {/* istatistikler */}
      <div className="flex flex-wrap items-center gap-8 border-b border-[#e5e7eb] bg-white px-6 py-2.5">
        {role !== 'owner' ? (
          <span className="text-[12px] text-[#9ca3af]" data-testid="abone-yetki-yok">
            E-posta raporu aboneleri yalnız proje owner’ı tarafından görülür ve yönetilir (§26.6).
          </span>
        ) : subscriptions.isLoading ? (
          <span className="animate-pulse text-[12px] text-[#9ca3af]">Veri yükleniyor…</span>
        ) : (
          [
            { label: 'Abone', value: String(rows.length) },
            { label: 'Onay bekleyen', value: String(pending) },
          ].map((stat) => (
            <div key={stat.label} className="flex items-center gap-2">
              <span className="text-[12px] text-[#9ca3af]">{stat.label}</span>
              <span className="num text-[13px] font-semibold text-[#111827]">{stat.value}</span>
            </div>
          ))
        )}
      </div>

      {/* içerik */}
      <div className="space-y-6 px-6 py-8">
        {/*
          §13/§72.3 — SİTE VE KURULUM en üstte: sayfaya gelen kullanıcının ilk iki
          sorusu "hangi siteyi izliyorum" ve "etiketim çalışıyor mu"dur. Rol kapısı
          YOKTUR (aşağıdaki kartların aksine): okuma ucu viewer'a açıktır ve izlenen
          sitenin adresi projenin kimliğidir, yapılandırma yetkisi değil.
        */}
        <SiteKurulumSection pid={pid} />

        {role === 'owner' && subscriptions.isError ? (
          <ErrorState
            title="Abone listesi alınamadı"
            description={(subscriptions.error as Error).message}
            onRetry={() => void subscriptions.refetch()}
          />
        ) : null}

        {role !== 'owner' ? null : (
        <Card
          title={
            <span className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-[#9ca3af]" aria-hidden="true" />
              E-posta raporları
            </span>
          }
          subtitle="Eklenen adrese önce onay e-postası gider; rapor YALNIZ onaylanmış aboneye gönderilir (§26.3)."
        >
          <form className="flex flex-wrap items-end gap-3" onSubmit={submit}>
            <label className="flex min-w-[240px] flex-1 flex-col gap-1">
              <span className="text-[12px] text-[#6b7280]">E-posta adresi</span>
              <input
                data-testid="abone-eposta"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="ad.soyad@sirket.com"
                /*
                 * `maxLength` sunucu doğrulamasının YERİNE geçmez (§26.6 uzunluk + regex
                 * kontrolü orada da vardır); yalnız kullanıcıyı kesin reddedilecek bir
                 * girdiyi yazmaya devam etmekten kurtarır.
                 */
                maxLength={254}
                className="h-8 rounded-lg border border-[#e5e7eb] bg-white px-3 text-[13px] text-[#111827] outline-none placeholder:text-[#9ca3af] focus:border-[#d1d5db]"
              />
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-[12px] text-[#6b7280]">Sıklık</span>
              <select
                data-testid="abone-cadence"
                value={cadence}
                onChange={(event) => setCadence(event.target.value as EmailCadence)}
                className="h-8 rounded-lg border border-[#e5e7eb] bg-white px-2 text-[13px] text-[#111827] outline-none focus:border-[#d1d5db]"
              >
                {CADENCE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <button
              type="submit"
              data-testid="abone-ekle"
              className="btn-primary"
              disabled={email.trim() === '' || add.isPending}
            >
              {add.isPending ? 'Ekleniyor…' : 'Abone ekle'}
            </button>
          </form>

          {addError !== null ? (
            <p className="mt-2 text-[12px] text-red-600">{addError}</p>
          ) : add.isSuccess ? (
            <p className="mt-2 text-[12px] text-[#6b7280]">
              Onay e-postası kuyruğa alındı. Abone linke tıklayana kadar rapor gönderilmez.
            </p>
          ) : null}

          {removeError !== null ? (
            <p className="mt-2 text-[12px] text-red-600">{removeError}</p>
          ) : null}

          <div className="mt-5" data-testid="abone-listesi">
            <Table
              rows={rows}
              rowKey={(row) => row.id}
              loading={subscriptions.isLoading}
              emptyTitle="Henüz abone yok"
              emptyDescription="Yukarıdaki formla ilk adresi ekleyin; onaydan sonra rapor gönderimi başlar."
              columns={[
                {
                  key: 'email',
                  header: 'E-posta',
                  render: (row) => (
                    <span className="font-mono text-[12px] text-[#111827]">{row.email}</span>
                  ),
                },
                {
                  key: 'cadence',
                  header: 'Sıklık',
                  render: (row) => (
                    <span className="text-[#374151]">
                      {CADENCE_LABELS[row.cadence] ?? row.cadence}
                    </span>
                  ),
                },
                {
                  key: 'confirmed',
                  header: 'Durum',
                  render: (row) =>
                    /*
                     * §26.6 rozetleri: gri "onay bekliyor" / yeşil "aktif". Renk tek
                     * başına taşıyıcı DEĞİL — metin de durumu söyler (erişilebilirlik).
                     */
                    row.confirmed ? (
                      <Badge tone="success" dot title="Rapor bu adrese gönderilir">
                        aktif
                      </Badge>
                    ) : (
                      <Badge tone="neutral" dot title="Onay linkine tıklanana kadar rapor gitmez">
                        onay bekliyor
                      </Badge>
                    ),
                },
                {
                  key: 'created_at',
                  header: 'Eklenme',
                  cellClassName: 'hidden md:table-cell',
                  render: (row) => (
                    <span className="num text-[12px] text-[#9ca3af]">
                      {formatDateTime(row.created_at)}
                    </span>
                  ),
                },
                {
                  key: 'sil',
                  header: '',
                  align: 'right',
                  render: (row) => (
                    <button
                      type="button"
                      data-testid="abone-sil"
                      aria-label={`${row.email} aboneliğini sil`}
                      className="btn-ghost"
                      disabled={remove.isPending}
                      onClick={() => remove.mutate(row.id)}
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                      Sil
                    </button>
                  ),
                },
              ]}
            />
          </div>
        </Card>
        )}

        {/*
          §72 — proje kartı EN ÜSTTE: sayfanın konusu bu projedir ve kullanıcının
          "SDK etiketim neydi / anahtarımı kaybettim" soruları en sık sorulanlardır.
          Yalnız proje owner'ına çizilir (dört ucun dördü de owner ister).
        */}
        {role === 'owner' ? <ProjectSection pid={pid} /> : null}

        {/* §27.7 — çoklu kullanıcı bölümü; e-posta abonelerinin ALTINDA durur. */}
        <MembersSection pid={pid} />

        {/*
          §85.5 — veri hakları. Üyelerin ALTINDA: gündelik bir iş değil, hukuki bir
          yükümlülüğün karşılandığı yerdir. Yalnız proje owner'ına çizilir (uçlar da
          owner ister).
        */}
        {role === 'owner' ? <DataRightsSection pid={pid} /> : null}

        {/* §19.6 — iç maliyet kartı EN ALTTA: operatöre bakar, müşteri işine değil. */}
        {role === 'owner' ? <CostSection pid={pid} /> : null}
      </div>
    </div>
  );
}
